import React, { useState, useEffect } from 'react';
import T from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import Modal from 'components/Modal';
import Input from 'components/Input';
import Button from 'components/Button';
import MessageBox from 'components/MessageBox';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { closeModal } from 'reducers/modals';
import { formatBackEndWeightToSystem, convertKgToLbs } from 'utils/weightHelper';

const BinFormModal = ({
  isOpen,
  onSubmit,
  bin = {},
  isEditing = false,
  isLoading = false,
  measurementSystem = 'imperial',
  closeModal
}) => {
  const [formData, setFormData] = useState({
    name: '',
    capacity: ''
  });
  const [errors, setErrors] = useState({});

  const isMetric = measurementSystem === 'metric';
  const maxCapacityLbs = 19999999; // Backend limit: < 20 million lbs (10,000 tons)
  const maxCapacityKg = Math.round(formatBackEndWeightToSystem(maxCapacityLbs, 'metric'));

  useEffect(() => {
    if (isOpen) {
      let displayCapacity = '';
      if (bin && bin.capacity) {
        // Convert capacity to display units (kg if metric, lbs if imperial)
        displayCapacity = isMetric
          ? Math.round(formatBackEndWeightToSystem(bin.capacity, measurementSystem))
          : Math.round(bin.capacity);
      }

      setFormData({
        name: (bin && bin.name) || '',
        capacity: displayCapacity || ''
      });
      setErrors({});
    }
  }, [isOpen, bin, measurementSystem]);

  const handleInputChange = (field) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Bin ID is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Bin ID cannot be longer than 50 characters';
    }

    if (formData.capacity && parseFloat(formData.capacity) < 0) {
      newErrors.capacity = 'Capacity cannot be negative';
    }

    // Validate capacity limits based on measurement system
    const maxCapacity = isMetric ? maxCapacityKg : maxCapacityLbs;
    if (formData.capacity && parseFloat(formData.capacity) > maxCapacity) {
      newErrors.capacity = `Capacity Should be less than ${maxCapacity + 1} ${isMetric ? 'kg' : 'lbs'}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      let capacityInLbs = formData.capacity ? parseFloat(formData.capacity) : null;

      // Convert kg input back to lbs for API (backend expects lbs)
      if (capacityInLbs && isMetric) {
        capacityInLbs = Math.round(convertKgToLbs(capacityInLbs));
      }

      const submitData = {
        name: formData.name.trim(),
        capacity: capacityInLbs
      };

      await onSubmit(submitData);
      closeModal();
    } catch (error) {
      // Handle server-side validation errors
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toastResponseErrors(error);
      }
    }
  };

  const modalTitle = isEditing
    ? <FormattedMessage id="general.editBin" />
    : <FormattedMessage id="general.addBin" />;

  const capacityLabel = isMetric ? 'Capacity (kg)' : 'Capacity (lbs)';
  const capacityPlaceholder = isMetric ? '(kg)' : '(lbs)';
  const maxCapacity = isMetric ? maxCapacityKg : maxCapacityLbs;

  return (
    <Modal title={modalTitle}>
      <section className="modal-body">
        <div>
          <FormattedMessage id="component.dataTable.headers.binID" />
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name')(e.target.value)}
            placeholder="e.g., Bin 1, Feed Storage A, etc."
            maxLength={25}
            required
            className={errors.name ? 'has-error' : ''}
          />
          {errors.name && (
            <MessageBox type="alert" className="mt-10">
              {Array.isArray(errors.name) ? errors.name[0] : errors.name}
            </MessageBox>
          )}
        </div>

        <div>
          {capacityLabel}
          <Input
            type="number"
            value={formData.capacity}
            onChange={(e) => handleInputChange('capacity')(e.target.value)}
            placeholder={capacityPlaceholder}
            min={0}
            max={maxCapacity}
            step={0.1}
            required
            className={errors.capacity ? 'has-error' : ''}
          />
          {errors.capacity && (
            <MessageBox type="alert" className="mt-10">
              {Array.isArray(errors.capacity) ? errors.capacity[0] : errors.capacity}
            </MessageBox>
          )}
        </div>
      </section>

      <section className="modal-footer text-right">
        <Button
          className="margin-right-1"
          onClick={closeModal}
          disabled={isLoading}
        >
          <FormattedMessage id="general.button.cancel" />
        </Button>

        <Button
          primary
          onClick={handleSubmit}
          disabled={isLoading || !formData.name.trim() || !formData.capacity}
        >
          {(() => {
            if (isLoading) {
              return <FormattedMessage id="general.button.saving" />;
            }
            if (isEditing) {
              return <FormattedMessage id="general.button.update" />;
            }
            return <FormattedMessage id="general.button.add" />;
          })()}
        </Button>
      </section>
    </Modal>
  );
};

BinFormModal.propTypes = {
  isOpen: T.bool.isRequired,
  onSubmit: T.func.isRequired,
  bin: T.object,
  isEditing: T.bool,
  isLoading: T.bool,
  measurementSystem: T.oneOf(['imperial', 'metric']),
  closeModal: T.func.isRequired
};

export default connect(null, { closeModal })(BinFormModal);
